import React, { useEffect, useMemo, useRef, useState } from "react";
import { Segment, Message, Grid, List, Label } from "semantic-ui-react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

function pickAddressString(svc) {
  // in jouw model zit address vaak in __raw.addressDetails of addressDetails
  const a =
    svc?.__raw?.addressDetails ||
    svc?.addressDetails ||
    svc?.__raw?.address ||
    svc?.address ||
    null;

  const lines = Array.isArray(a?.lines) ? a.lines.join(" ") : (a?.lines || a?.line || "");
  const pc = a?.postalCode || a?.zip || "";
  const city = a?.city || a?.municipality || "";

  const combined = [lines, pc, city].filter(Boolean).join(" ").trim();
  return combined;
}

function toLatLngMaybe(obj) {
  if (!obj) return null;
  const lat = obj.lat ?? obj.latitude;
  const lng = obj.lng ?? obj.longitude;
  if (typeof lat === "number" && typeof lng === "number") return { lat, lng };
  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isNaN(latN) && !Number.isNaN(lngN)) return { lat: latN, lng: lngN };
  return null;
}

const indexToChar = (i) => String.fromCharCode(65 + i); // A,B,C,...

export default function ServicesMap({
  services = [],
  onSelect,
  googleMapsApiKey
}) {
  const [mapsError, setMapsError] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [markers, setMarkers] = useState([]); // [{ svc, pos }]
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const geocoderRef = useRef(null);

  const containerStyle = useMemo(
    () => ({ width: "100%", height: "100%", borderRadius: 14 }),
    []
  );

  const defaultCenter = useMemo(() => ({ lat: 51.1774, lng: 4.3822 }), []);

  // Orange marker icon configuration
  const orangeMarkerIcon = useMemo(() => ({
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
    fillColor: "#FBA213",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale: 1.2
  }), []);

  const orangeMarkerIconHover = useMemo(() => ({
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
    fillColor: "#FBA213",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 3,
    scale: 1.4
  }), []);

  // 1) meteen markers nemen als coords bestaan
  useEffect(() => {
    const direct = services
      .map((svc) => {
        const pos =
          toLatLngMaybe(svc?.latLng) ||
          toLatLngMaybe(svc?.coordinates) ||
          toLatLngMaybe(svc?.__raw?.coordinates) ||
          toLatLngMaybe(svc?.__raw?.location) ||
          null;

        return pos ? { svc, pos } : null;
      })
      .filter(Boolean);

    if (direct.length) setMarkers(direct);
    else setMarkers([]); // reset bij nieuwe lijst zonder coords
  }, [services]);

  // 2) geocode voor services zonder coords (na maps load)
  useEffect(() => {
    if (!isLoaded) return;
    if (!window.google?.maps) return;

    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    let cancelled = false;

    const run = async () => {
      const existingByUuid = new Set(markers.map((m) => m.svc?.uuid).filter(Boolean));

      const missing = services.filter((svc) => {
        if (svc?.uuid && existingByUuid.has(svc.uuid)) return false;

        const hasRawPos =
          toLatLngMaybe(svc?.latLng) ||
          toLatLngMaybe(svc?.coordinates) ||
          toLatLngMaybe(svc?.__raw?.coordinates) ||
          toLatLngMaybe(svc?.__raw?.location);

        return !hasRawPos;
      });

      if (!missing.length) return;

      const newOnes = [];

      for (const svc of missing) {
        const addr = pickAddressString(svc);
        if (!addr) continue;

        // eslint-disable-next-line no-await-in-loop
        const res = await new Promise((resolve) => {
          geocoderRef.current.geocode({ address: addr }, (results, status) => {
            resolve({ results, status });
          });
        });

        if (cancelled) return;

        if (res.status === "OK" && res.results?.[0]) {
          const loc = res.results[0].geometry.location;
          const pos = { lat: loc.lat(), lng: loc.lng() };
          newOnes.push({ svc, pos });
        }
      }

      if (!cancelled && newOnes.length) {
        setMarkers((prev) => [...prev, ...newOnes]);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, services, markers]);

  const center = useMemo(() => {
    if (hoveredIndex != null && markers[hoveredIndex]?.pos) return markers[hoveredIndex].pos;
    if (markers[0]?.pos) return markers[0].pos;
    return defaultCenter;
  }, [markers, hoveredIndex, defaultCenter]);

  const listServices = useMemo(() => {
    // lijst volgorde = markers volgorde (A,B,C...)
    // Als nog niet geocoded, toon toch services in oorspronkelijke volgorde onderaan (zonder letter)
    if (markers.length) return markers.map((m) => m.svc);
    return services;
  }, [markers, services]);

  return (
    <Segment
      style={{
        height: "100%",
        padding: 0,
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(17,24,39,0.10)",
        boxShadow: "0 8px 18px rgba(17,24,39,0.08)"
      }}
    >
      {mapsError ? <Message negative style={{ margin: 12 }} content={mapsError} /> : null}

      <Grid columns={16} style={{ margin: 0, height: "100%" }}>
        {/* MAP */}
        <Grid.Column width={12} style={{ padding: 0, height: "100%" }}>
          <LoadScript
            googleMapsApiKey={googleMapsApiKey}
            onLoad={() => setIsLoaded(true)}
            onError={() => setMapsError("Google Maps kon niet laden (key/referrer/API).")}
          >
            <GoogleMap
              mapContainerStyle={{ ...containerStyle, borderRadius: 0 }}
              center={center}
              zoom={14}
              options={{
                fullscreenControl: true,
                streetViewControl: false,
                mapTypeControl: false
              }}
              onLoad={() => setIsLoaded(true)}
            >
              {markers.map((m, idx) => (
                <Marker
                  key={`${m.svc?.uuid || m.svc?.name || "svc"}-${idx}`}
                  position={m.pos}
                  onMouseOver={() => setHoveredIndex(idx)}
                  onMouseOut={() => setHoveredIndex(null)}
                  onClick={() => onSelect && onSelect(m.svc)}
                  icon={hoveredIndex === idx ? orangeMarkerIconHover : orangeMarkerIcon}
                  opacity={hoveredIndex == null ? 1 : hoveredIndex === idx ? 1 : 0.45}
                  zIndex={hoveredIndex === idx ? 999 : 1}
                  label={{
                    text: indexToChar(idx),
                    color: hoveredIndex === idx ? "white" : "black",
                    fontSize: "14px",
                    fontWeight: "bold"
                  }}
                />
              ))}
            </GoogleMap>
          </LoadScript>
        </Grid.Column>

        {/* LIST */}
        <Grid.Column
          width={4}
          style={{
            padding: 12,
            borderLeft: "1px solid rgba(17,24,39,0.08)",
            height: "100%",
            overflowY: "auto",
            background: "#fff"
          }}
        >
          <List selection relaxed="very">
            {listServices.map((svc, idx) => {
              const hasLetter = markers.length > 0 && idx < markers.length;
              const active = hoveredIndex === idx;

              return (
                <List.Item
                  key={`${svc?.uuid || svc?.name}-${idx}`}
                  onMouseEnter={() => hasLetter && setHoveredIndex(idx)}
                  onMouseLeave={() => hasLetter && setHoveredIndex(null)}
                  onClick={() => onSelect && onSelect(svc)}
                  style={{
                    borderRadius: 10,
                    padding: "10px 10px",
                    background: active ? "#f8fafc" : "transparent"
                  }}
                >
                  {hasLetter ? (
                    <Label circular color={active ? "blue" : "orange"}>
                      {indexToChar(idx)}
                    </Label>
                  ) : (
                    <Label circular basic color="grey">
                      ?
                    </Label>
                  )}

                  <List.Content style={{ marginLeft: 8 }}>
                    <List.Header style={{ fontWeight: 800 }}>
                      {svc?.name || "Dienst"}
                    </List.Header>
                    <List.Description style={{ color: "#6b7280" }}>
                      {svc?.type || ""}
                      {svc?.location ? ` • ${svc.location}` : ""}
                    </List.Description>
                  </List.Content>
                </List.Item>
              );
            })}
          </List>
        </Grid.Column>
      </Grid>
    </Segment>
  );
}